// AuditPage — org-scoped CP audit log.
//
// Lists events from GET /api/v1/organizations/{orgId}/events with
// cursor pagination + optional event-type filter. Renders via the
// design-system DataTable widget so density / sort / empty-state
// behaviour matches every other table in the console.
//
// Endpoint contract (cp/internal/api/handlers/events.go):
//   query: cursor?, limit?, type?
//   response envelope: { data: Event[], pagination: {next_cursor?, has_more} }

import type { components } from '@intelliauth/api-client-v2-cp/types'
import { Badge, Button, DataTable, EmptyState, Input } from '@intelliauth/ui'
import type { DataTableColumn } from '@intelliauth/ui'
import { AlertCircle, ChevronLeft, ChevronRight, History } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { cpClient } from '@/lib/api/cpClient'

type Event = components['schemas']['Event']

const PAGE_LIMIT = 50

export default function AuditPage() {
  const { currentOrg } = useAuth()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState('')
  const [typeFilterApplied, setTypeFilterApplied] = useState('')
  // Cursor history — index 0 is the first page (no cursor), each
  // subsequent entry is the cursor returned by that page's response.
  // Push to go forward; pop to go back.
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined])
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined)

  const currentCursor = cursorStack[cursorStack.length - 1]
  const pageIndex = cursorStack.length - 1

  const fetchEvents = useCallback(
    async (cursor: string | undefined, type: string) => {
      if (!currentOrg) return
      setLoading(true)
      setError(null)
      try {
        const env = await cpClient().get('/api/v1/organizations/{id}/events', {
          path: { id: currentOrg.id },
          query: {
            ...(cursor ? { cursor } : {}),
            ...(type ? { type } : {}),
            limit: PAGE_LIMIT,
          },
        })
        // Envelope: { data: Event[], pagination: {next_cursor?, has_more}, meta }
        const body = env as { data?: Event[]; pagination?: { next_cursor?: string } }
        setEvents(body.data ?? [])
        setNextCursor(body.pagination?.next_cursor || undefined)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load events')
        setEvents([])
        setNextCursor(undefined)
      } finally {
        setLoading(false)
      }
    },
    [currentOrg],
  )

  useEffect(() => {
    fetchEvents(currentCursor, typeFilterApplied)
  }, [fetchEvents, currentCursor, typeFilterApplied])

  const handleApplyFilter = () => {
    setCursorStack([undefined])
    setTypeFilterApplied(typeFilter.trim())
  }

  const handleNextPage = () => {
    if (!nextCursor) return
    setCursorStack(prev => [...prev, nextCursor])
  }

  const handlePrevPage = () => {
    setCursorStack(prev => (prev.length > 1 ? prev.slice(0, -1) : prev))
  }

  const columns: DataTableColumn<Event>[] = [
    {
      key: 'created_at',
      header: 'When',
      width: '180px',
      cell: e => (
        <span className="text-sm tabular-nums text-muted-foreground">
          {new Date(e.created_at).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'event_type',
      header: 'Type',
      width: '260px',
      cell: e => (
        <Badge variant="info" className="font-mono text-xs">
          {e.event_type}
        </Badge>
      ),
    },
    {
      key: 'actor',
      header: 'Actor',
      width: '220px',
      cell: e => (
        <span className="text-sm text-foreground truncate">
          {e.actor?.email || e.actor?.id || (
            <span className="text-muted-foreground/70">system</span>
          )}
        </span>
      ),
    },
    {
      key: 'tenant',
      header: 'Tenant',
      width: '160px',
      cell: e => (
        <span className="text-xs font-mono text-muted-foreground">
          {e.tenant?.slug || e.tenant_id || (
            <span className="text-muted-foreground/50">—</span>
          )}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Details',
      cell: e => (
        <span className="text-xs text-muted-foreground truncate block max-w-[420px]">
          {e.description ||
            (e.resource ? `${e.resource.type ?? ''}: ${e.resource.id ?? ''}` : '')}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">Audit log</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every org-scoped CP action — provisioning, suspensions, plan changes, member edits.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          placeholder="Filter by event type (e.g. tenant.provisioning.completed)"
          className="max-w-md"
          onKeyDown={e => {
            if (e.key === 'Enter') handleApplyFilter()
          }}
        />
        <Button variant="outline" size="sm" onClick={handleApplyFilter}>
          Apply
        </Button>
        {typeFilterApplied && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setTypeFilter('')
              setTypeFilterApplied('')
              setCursorStack([undefined])
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <DataTable<Event>
        columns={columns}
        rows={events}
        getRowId={e => e.id}
        loading={loading}
        empty={
          <EmptyState
            icon={History}
            title="No events yet"
            description={
              typeFilterApplied
                ? `No events match "${typeFilterApplied}". Try clearing the filter.`
                : 'Org-scoped CP actions appear here as you provision tenants, invite members, and change settings.'
            }
          />
        }
      />

      <div className="flex items-center justify-between border-t border-border pt-3">
        <div className="text-xs text-muted-foreground">
          Page {pageIndex + 1}
          {events.length > 0 && (
            <span> · {events.length} event{events.length === 1 ? '' : 's'}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevPage}
            disabled={pageIndex === 0}
            leftIcon={ChevronLeft}
          >
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={!nextCursor}
            rightIcon={ChevronRight}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
